import { useEffect, useState } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import { useParams, useNavigate } from "react-router-dom";
import { LinkProps } from "@generouted/react-router/client";
import { Path, Params } from "@/router";
import { FormValues } from "../../_form/validation/schema";
import EventForm from "../../_form";
import { useQuery, useMutation } from "@apollo/client";
import { GET_EVENT_DEFAULTS, UPDATE_EVENT_MUTATION } from "../../queries";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { toUpper } from "lodash";
import { BackofficeEventsUpdateEventInput } from "../../_form/types";
import {
  buildEventFeeInputs,
  getDefaultFormValues,
  getEventTypeInput,
  getRegistrationMaxAttendanceOverride,
  isSeminarFormEvent,
  setToDayBoundary,
  toNumber,
  transformSessionsToInput
} from "../../_form/utils";
import { EVENT_WAITLIST_COUNT_QUERY } from "../queries";
import { getEventTypeForForm, isSeminar as isSeminarEvent } from "../utils";
import { useEventDetailQueryDocument } from "../useEventDetailQuery";
import { useReturnTo } from "@/hooks/useSearchParams";
import MultiStepFormSkeleton from "@/components/Skeleton/MultiStepFormSkeleton";
import { useEventsBasePath } from "../../_eventsBasePathContext";
import { RegistrationType } from "../../_form/types";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export default function EditEventPage() {
  const { eventSlug } = useParams();
  const basePath = useEventsBasePath();
  usePageTitle(`${basePath}/${eventSlug}/edit`, "Events | Edit Event");

  const navigate = useNavigate();
  const homePath = { to: basePath as Path } as LinkProps<Path, Params>;
  const returnTo = useReturnTo();
  const { trackEvent } = useAmplitude();

  // Fetch the full event data instead of relying on location.state
  const eventDetailQuery = useEventDetailQueryDocument();

  const { data: eventData, loading } = useQuery(eventDetailQuery, {
    variables: { slug: eventSlug }
  });
  const isSeminar = isSeminarEvent(eventData?.event);
  const activityTypeId = getEventTypeForForm(eventData?.event).id;
  const firstSessionDate = eventData?.event?.sessions?.[0]?.startsAt?.slice(0, 10);
  const { data: eventDefaultsData, loading: eventDefaultsLoading } = useQuery(GET_EVENT_DEFAULTS, {
    variables: {
      activityTypeId,
      ...(firstSessionDate ? { sessionStartDate: firstSessionDate } : {})
    },
    skip: !isSeminar || !activityTypeId
  });

  const { data: waitlistCountData } = useQuery(EVENT_WAITLIST_COUNT_QUERY, {
    variables: { eventSlug: eventSlug ?? "" },
    skip: !eventSlug || !eventData?.event?.hasWaitlist
  });
  const hasWaitlistEntries = (waitlistCountData?.eventWaitlistEntries?.totalCount ?? 0) > 0;

  const [updateEventMutation] = useMutation(UPDATE_EVENT_MUTATION);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !eventData?.event) {
      navigate(homePath.to);
    }
  }, [eventData?.event, loading, homePath.to, navigate]);

  const hasRegistrations = (eventData?.event?.registrationCount ?? 0) > 0;

  // Don't render form until we have the event data
  if (loading || !eventData?.event || (isSeminar && eventDefaultsLoading)) {
    return <MultiStepFormSkeleton stepCount={7} renderStepper={true} />;
  }

  // Pass raw GraphQL eventType/activityType through — getDefaultFormValues calls
  // getEventTypeForForm. Pre-wrapping as form eventType retags activity-type UUIDs
  // as legacyEventType and updateEvent then writes them to event_type_id (FK error).
  const simplifiedEvent = {
    id: eventData.event.id,
    title: eventData.event.title,
    description: eventData.event.description,
    eventType: eventData.event.eventType ?? eventData.event.eventTypeSummary,
    activityType: eventData.event.activityType ?? eventData.event.activityTypeSummary,
    section: eventData.event.section,
    universities: eventData.event.universities,
    locationType: (eventData.event.locationType?.toUpperCase() as "ONSITE" | "VIRTUAL") || "ONSITE",
    facility: eventData.event.facility,
    address1: eventData.event.address1,
    address2: eventData.event.address2,
    city: eventData.event.city,
    state: eventData.event.state,
    zip: eventData.event.zip,
    timezone: eventData.event.timezone,
    location: eventData.event.location,
    sessions: eventData.event.sessions,
    registrationType: eventData.event.registrationType.toLowerCase() as RegistrationType,
    bannerImageUrl: "",
    registrationOpenAt: eventData.event.registrationOpenAt,
    registrationCloseAt: eventData.event.registrationCloseAt,
    hasWaitlist: eventData.event.hasWaitlist,
    customFields: eventData.event.customFields || [],
    termsContent: eventData.event.termsContent,
    orderTemplateId: eventDefaultsData?.eventDefaults?.orderTemplateId ?? "",
    products: [],
    eventFees: eventData.event.eventFees ?? [],
    collectsAttendance: eventData.event.collectsAttendance,
    patEventAttributes: eventData.event.patEventAttributes,
    recruitmentEventAttributes: eventData.event.recruitmentEventAttributes,
    draft: eventData.event.draft
  };

  const initialValues = getDefaultFormValues(simplifiedEvent, false);

  const handleSubmit = async (data: FormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const isPAT = data.eventType?.name === "PAT";
      const isRecruitment = data.eventType?.name === "Recruitment";
      const isSeminar = isSeminarFormEvent(data);
      const transformedSessions = transformSessionsToInput(
        data.sessions,
        data.timezone,
        getRegistrationMaxAttendanceOverride(data)
      );

      const addressFields =
        data.locationType === "virtual"
          ? {
              address1: "",
              address2: "",
              city: "",
              state: "",
              zip: ""
            }
          : {
              address1: data.address1,
              address2: data.address2,
              city: data.city,
              state: data.state,
              zip: data.zipcode
            };

      const {
        yardageMale,
        courseRatingMale,
        yardageMaleFiftyPlus,
        yardageFemale,
        courseRatingFemale,
        yardageFemaleFiftyPlus,
        hostProfessional,
        examiner
      } = data.patEventAttributes || {};

      const {
        recruitmentEventType,
        purpose,
        strategicFunding,
        pgaRepresentation,
        supportRequested,
        estimatedExpenses,
        sponsorshipAsk,
        estimatedParents,
        estimatedCoaches,
        estimatedOtherAttendees,
        additionalComments,
        eventRecap,
        overallFeedback,
        eventRating,
        commentsAndQuotes,
        photoUploads,
        isFinalized
      } = data.recruitmentEventAttributes || {};

      const registrationFeeCents = data.products?.[0]?.variantPrice;
      const courseFeeCents = data.products?.[1]?.variantPrice;
      const collectionType = data.products?.[1]?.collection;

      const updateEventInput: BackofficeEventsUpdateEventInput = {
        id: eventData?.event?.id,
        description: data.description,
        draft: data.draft,
        title: data.eventName,
        ...getEventTypeInput(data.eventType),
        sectionId: data.sectionId,
        universityIds: data.universityIds ?? [],
        facilityId: data.facilityId,
        location: data.location,
        locationType: toUpper(data.locationType),
        timezone: data.timezone,
        registrationOpenAt: setToDayBoundary(data.registrationOpenDate.startDate, "start", data.timezone),
        registrationCloseAt: setToDayBoundary(data.registrationCloseDate.startDate, "end", data.timezone),
        sessions: transformedSessions,
        waitlistType: toUpper(data.waitlistType),
        ...addressFields,
        customFields: data.customFields,
        termsContent: data.termsContent,
        collectsAttendance: data.collectsAttendance,
        ...(isSeminar && { eventFees: buildEventFeeInputs(data.eventFees ?? []) }),
        ...(isPAT && {
          patEventAttributes: {
            yardageMale: toNumber(yardageMale),
            courseRatingMale: toNumber(courseRatingMale),
            yardageMaleFiftyPlus: toNumber(yardageMaleFiftyPlus),
            yardageFemale: toNumber(yardageFemale),
            courseRatingFemale: toNumber(courseRatingFemale),
            yardageFemaleFiftyPlus: toNumber(yardageFemaleFiftyPlus),
            hostProfessionalId: hostProfessional?.id,
            examinerId: examiner?.id,
            collectionType: courseFeeCents ? toUpper(collectionType) : undefined,
            registrationFeeCents: registrationFeeCents,
            courseFeeCents: courseFeeCents
          }
        }),
        ...(isRecruitment && {
          recruitmentEventAttributes: {
            recruitmentEventType: recruitmentEventType || "RECRUITMENT_CAREER_FAIR",
            purpose: purpose || "RECRUITING_EVENT",
            strategicFunding: strategicFunding || "NOT_SURE",
            pgaRepresentation: pgaRepresentation || [],
            supportRequested: supportRequested || [],
            estimatedExpenses: toNumber(estimatedExpenses),
            sponsorshipAsk: toNumber(sponsorshipAsk),
            estimatedParents: toNumber(estimatedParents),
            estimatedCoaches: toNumber(estimatedCoaches),
            estimatedOtherAttendees: toNumber(estimatedOtherAttendees),
            additionalComments: additionalComments || "",
            eventRecap: eventRecap || "",
            overallFeedback: overallFeedback || "",
            eventRating: toNumber(eventRating),
            commentsAndQuotes: commentsAndQuotes || "",
            photoUploads: photoUploads || [],
            isFinalized: isFinalized || false
          }
        })
      };

      const { data: responseData } = await updateEventMutation({
        variables: { input: updateEventInput }
      });
      const { success } = responseData.updateEvent;
      if (success) {
        if (isRecruitment) {
          navigate(`${basePath}/${eventSlug}/recap` as Path);
          showSnackbar("Event submitted", { close: true });
        } else {
          navigate(`${basePath}/${eventSlug}/overview` as Path);
          showSnackbar("Event updated", { close: true });
        }
        trackEvent(
          EVENT_ANALYTICS_EVENTS.updated,
          getEventAnalyticsData({
            source: "edit_event",
            eventId: updateEventInput.id,
            eventSlug,
            action: data.draft ? "save_draft" : "update",
            isDraft: !!data.draft,
            isRecruitment
          })
        );
      } else {
        showErrorSnackbar(responseData.updateEvent.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EventForm
      initialValues={initialValues}
      initialStepVisibility={eventDefaultsData?.eventDefaults?.formVisibility}
      onSubmit={handleSubmit}
      isEditing={true}
      hasRegistrations={hasRegistrations}
      hasWaitlistEntries={hasWaitlistEntries}
      isSubmitting={isSubmitting}
      backLinkTo={returnTo || (eventSlug ? `${basePath}/${eventSlug}/overview` : basePath)}
    />
  );
}
